/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import {
  DICTIONARY_OFFICE_MEASUREMENT_DEFAULT_FOR_EDIT,
  DICTIONARY_OFFICE_TYPE_FOR_EDIT,
  DICTIONARY_TEMPLATES,
  DICTIONARY_TIMEZONES,
  MasterZoneRestService,
  Month,
  OfficeMeasurement,
  OfficeRestService,
  OfficeStatus,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  globalDictionaries,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { Enum, FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { currentYear, expandObservable } from '@gh/core-util';
import { map$, switchMap$ } from '@gh/rx/operators';

import { keyBy, mapValues, range } from 'lodash';
import { Observable } from 'rxjs';

import { OfficeDetails, officeDetailsFromDto, officeDetailsToDto } from './office-details.model';
import { OfficeListItem } from './office-list-item.model';
import { OfficeActiveRecord } from './office-active-record-model';
import { combineLatest$, of$ } from '@gh/rx';

@Injectable()
export class OfficeDetailsBusinessService implements InitViewService {
  constructor(private officeRest: OfficeRestService,
              private masterZoneRest: MasterZoneRestService,
              private dictionaryService: DictionaryService,
              private userContext: BuilderUserContext) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    return this.dictionaryService
               .get(DICTIONARY_TIMEZONES)
               .pipe(switchMap$((timeZones) => combineLatest$([
                              this.dictionaryService.getByDef(DICTIONARY_OFFICE_TYPE_FOR_EDIT, {
                                applicationView: this.userContext.applicationView,
                              }),
                              this.dictionaryService.getByEnum(OfficeStatus),
                              this.dictionaryService.getByEnum(OfficeMeasurement),
                              this.dictionaryService.getByEnum(Month),
                              expandObservable(mapValues(
                                keyBy(Enum.names(OfficeMeasurement)),
                                (name) => this.dictionaryService.getByDef(DICTIONARY_OFFICE_MEASUREMENT_DEFAULT_FOR_EDIT, {
                                  measurement: name,
                                }),
                              )),
                              this.dictionaryService.get(DICTIONARY_TEMPLATES),
                              isRouteForCreationMode(route) ? this.getNew() : this.get(getRouteParam(route, 'officeId')),
                              this.userContext.corporateView ? this.masterZoneRest.findAllActive() : of$([this.userContext.masterZone]),
                              this.dictionaryService.get(globalDictionaries.states),
                            ]).pipe(map$(([
                              officeTypes,
                                                         officeStatuses,
                                                         measurements,
                                                         months,
                                                         measurementDefaults,
                                                         templates,
                                                         officeDetails,
                                                         masterZones,
                                                         states,
                                                       ]) => ({
        officeTypes,
        officeStatuses,
        measurements,
        financialYears: range(currentYear() - 2, currentYear() + 3), // tslint:disable-line:no-magic-numbers
        months,
        measurementDefaults,
        templates,
        officeDetails,
        masterZones,
        timeZones,
        states,
      }))),
    ));
  }

  create(details: OfficeDetails): Observable<OfficeDetails> {
    return this.officeRest.create(officeDetailsToDto(details)).pipe(map$(officeDetailsFromDto));
  }

  update(details: OfficeDetails): Observable<OfficeDetails> {
    return this.officeRest.update(officeDetailsToDto(details)).pipe(map$(officeDetailsFromDto));
  }

  get(id: ID): Observable<OfficeDetails> {
    return this.officeRest.get(id).pipe(map$(officeDetailsFromDto));
  }

  getNew(): Observable<OfficeDetails> {
    return this.officeRest.getNew().pipe(map$(officeDetailsFromDto));
  }

  findOfficesByName(query: FilteredDataQuery<string>): Observable<OfficeListItem[]> {
    return this.officeRest.findAllAsAutocompleteDataSource(<string>query.filter)
               .select(query).pipe(map$(getSlicedData));
  }

  findActiveRecordsByOfficeName(office: OfficeActiveRecord): Observable<OfficeActiveRecord> {
    return this.officeRest.findActiveAndInactiveRecords(office);
  }
  
  activateOffice(officeId: ID): Observable<OfficeDetails> {
    return this.officeRest.activateOffice(officeId).pipe(map$(officeDetailsFromDto));
  }

  blockOffice(officeId: ID): Observable<OfficeDetails> {
    return this.officeRest.blockOffice(officeId).pipe(map$(officeDetailsFromDto));
  }
}
